import type { IndexableFacets } from '#commerce/utils/filters/getIndexableFacetLinks'
import type { Catalog, ProductFilters } from '#root/api/clients/product/data-contracts'
import type { FilterItem, FiltersMap, PriceFilter, SearchFilter } from '#types/filters'
import { isIndexableFacetSelection } from '#commerce/utils/filters/getIndexableFacetLinks'

/**
 * Manages facet/filter and sort state for Product Listing Pages (PLP).
 *
 * Handles:
 * - Filter selection (color, size, price, etc.) with URL sync via `?filters` query param
 * - Sort selection with URL sync via `?sort` query param
 * - Store-based filtering (BOPIS) with URL sync via `?storeFilter` query param
 * - GTM tracking for filter apply/remove/clear and sort events
 * - keep-alive cache clearing when navigating back to filter-less PLP URLs (e.g., breadcrumbs)
 * - Data-driven sort options (when `enableDataDrivenSortOptions` feature flag is true)
 * - Bidirectional sync: URL → state and state → URL
 *
 * @param _filters - Product filters array from API (reactive or plain)
 * @param _sortOptions - Optional catalog sort options from API (used when `enableDataDrivenSortOptions` is true)
 * @param onChange - Optional callback invoked after filter/sort/storeFilter changes (after URL update)
 * @param indexableFacets - Optional API-approved single-facet selections that should receive `indexable=true`.
 * @returns Filter/sort state and actions: `filters`, `filtersQuery`, `sortOptions`, `sortState`, `storeFilterState`, `filterBy`, `filterByStore`, `removeFilter`, `clearFilters`, and `sortBy`
 *
 * @example
 * ```vue
 * <script setup>
 * const { data } = await products.catalog(...)
 * const {
 *   filters,
 *   filterBy,
 *   clearFilters,
 *   sortState,
 *   sortBy,
 *   sortOptions
 * } = useFilters(
 *   () => data.value?.filters || [],
 *   () => data.value?.sortOptions,
 *   () => console.log('Filters changed, refetch products')
 * )
 * </script>
 *
 * <template>
 *   <select v-model="sortState" @change="sortBy">
 *     <option v-for="opt in sortOptions" :value="opt.value">{{ opt.label }}</option>
 *   </select>
 *   <button @click="clearFilters">Clear all</button>
 * </template>
 * ```
 *
 * @remarks
 * - URL query params: `?filters={"color":["blue"]}&sort=price-asc&storeFilter=store-123`
 * - Watches URL changes (including CMS links) and syncs to internal state
 * - keep-alive: clears leftover filters when navigating back to filter-less URL
 * - GTM events: `plpPage.onApplyFilter`, `plpPage.onRemoveFilter`, `plpPage.onClearFilters`, `plpPage.onSort`
 * - Sort validation: falls back to `defaultSort` if URL sort is invalid
 * - Data-driven sort options: uses API-provided sort options when `$feature.enableDataDrivenSortOptions` is true
 */
export default (
  _filters: MaybeRefOrGetter<ProductFilters>,
  _sortOptions?: MaybeRefOrGetter<Catalog['sortOptions']>,
  onChange?: () => void,
  indexableFacets?: MaybeRefOrGetter<IndexableFacets | undefined>
) => {
  const { defaultSort, sorts } = useAppConfig().pages.plp
  const { $gtm, $feature, $t } = useNuxtApp()

  const region = useRegion()
  const route = useRoute()
  const router = useRouter()
  const approvedIndexableFacets = shallowRef<IndexableFacets>()

  const filtersQuery = ref(JSON.parse(route.query.filters?.toString() || '{}'))
  const filters = computed(() => Object.fromEntries(toValue(_filters)
    .map((item) => [item.code, {
      ...item,
      type: item.layout || 'TileExpanded',
      selected: filtersQuery.value[item.code] || []
    }])) as FiltersMap
  )
  const sortState = ref((route.query.sort || defaultSort) as string)
  const storeFilterState = ref(route.query.storeFilter?.toString() || undefined)

  watch(
    () => toValue(indexableFacets),
    (facets) => {
      if (facets && Object.keys(facets).length) approvedIndexableFacets.value = facets
    },
    { immediate: true }
  )

  watch(() => route.path, () => {
    approvedIndexableFacets.value = undefined
  })

  // This watch gets triggered both by onFiltersChange
  // and by external changes to the url, such as from links in CMS content
  watch(() => [route.query.filters, route.query.storeFilter, route.query.sort], (value, oldValue) => {
    filtersQuery.value = JSON.parse(route.query.filters?.toString() || '{}')
    sortState.value = (route.query.sort || defaultSort) as string
    storeFilterState.value = route.query.storeFilter?.toString() || undefined
    if (value[0] !== oldValue?.[0] || value[1] !== oldValue?.[1] || value[2] !== oldValue?.[2])
      onChange?.()
  })

  const sortOptions = computed(() => {
    const configSortOptions = sorts[region].map((sort) => ({ value: sort, label: $t.sorts[sort] }))

    return $feature.enableDataDrivenSortOptions
      ? toValue(_sortOptions)?.values.map(({ id, label }) => ({ value: id, label })) ?? configSortOptions
      : configSortOptions
  })

  /**
   * Updates URL with current filters, sort, and storeFilter state.
   *
   * Called internally by `filterBy`, `filterByStore`, `removeFilter`, `clearFilters`, `sortBy`.
   * Triggers `onChange` callback (if provided) via route query watch.
   *
   * @remarks
   * - Serializes `filtersQuery` to JSON string in `?filters` query param
   * - Removes `?filters` if no filters are selected
   */
  const onFiltersChange = () => {
    const filtersEntries = Object.values(filters.value)
      .filter(({ selected }) => selected?.length)
      .map(({ code, selected }) => [code, selected])

    filtersQuery.value = { ...filtersQuery.value, ...Object.fromEntries(filtersEntries) }
    const isIndexable = isIndexableFacetSelection(
      filtersQuery.value,
      toValue(_filters),
      approvedIndexableFacets.value,
    )

    router.push({
      query: {
        ...route.query,
        filters: Object.keys(filtersQuery.value).length ? JSON.stringify(filtersQuery.value) : undefined,
        indexable: isIndexable ? 'true' : undefined,
        sort: sortState.value,
        storeFilter: storeFilterState.value
      }
    })
  }

  /**
   * Type guard to check if a filter is a price input filter.
   *
   * @param filter - Filter item
   * @returns True if filter type is 'PriceInput'
   */
  const isPriceFilter = (filter: FilterItem): filter is PriceFilter => filter.type === 'PriceInput'

  /**
   * Type guard to check if a filter is a search input filter.
   *
   * @param filter - Filter item
   * @returns True if filter type is 'Search'
   */
  const isSearchFilter = (filter: FilterItem): filter is SearchFilter => filter.type === 'Search'

  /**
   * Applies or removes a filter and updates the URL.
   *
   * If a new value is added, triggers GTM `plpPage.onApplyFilter`.
   * If a value is removed, triggers GTM `plpPage.onRemoveFilter`.
   * Compares `filter.selected` (new state) with `filtersQuery.value[code]` (previous state).
   *
   * @param code - Filter code (e.g., 'color', 'size', 'price')
   *
   * @example
   * ```ts
   * // Toggle color filter (selection managed by v-model in UI)
   * filterBy('color')
   * ```
   *
   * @remarks
   * - For PriceInput and Search filters, GTM receives the selected value(s) directly
   * - For other filters, GTM receives the option label for the added/removed value
   * - Automatically removes filter from `filtersQuery` if all values are deselected
   */
  const filterBy = (code: string) => {
    const filter = filters.value[code]
    const { selected, facetId } = filter
    const previous = filtersQuery.value[code] || []

    if (isPriceFilter(filter) || isSearchFilter(filter)) {
      $gtm.push('plpPage.onApplyFilter', code, facetId, selected)
    }
    else if (previous.length < selected.length) {
      const id = selected.filter((item) => !previous.includes(item))[0]
      const { label } = filter.options.find((option) => option.id === id)!
      $gtm.push('plpPage.onApplyFilter', filter.label, facetId, label)
    }
    else {
      const id = previous.filter((item: string) => !selected.includes(item))[0]
      const { label } = filter.options.find((option) => option.id === id)!
      $gtm.push('plpPage.onRemoveFilter', filter.label, facetId, label)
      if (!selected.length) delete filtersQuery.value[code]
    }
    onFiltersChange()
  }

  /**
   * Filters products by store ID (for BOPIS "in-store availability" filtering).
   *
   * @param storeId - Store ID to filter by (e.g., 'store-12345'), or undefined to clear
   *
   * @example
   * ```ts
   * // Filter by store
   * filterByStore('store-12345')
   *
   * // Clear store filter
   * filterByStore(undefined)
   * ```
   *
   * @remarks
   * Updates `?storeFilter` query param and triggers product refetch via `onChange` callback
   */
  const filterByStore = (storeId?: string) => {
    storeFilterState.value = storeId
    onFiltersChange()
  }

  /**
   * Removes a specific filter value (used for "X" close buttons on active filter chips).
   *
   * @param filterToRemove - Filter removal payload
   * @param filterToRemove.code - Filter code (e.g., 'color')
   * @param filterToRemove.id - Option ID to remove (e.g., 'blue'), or entire filter if omitted
   *
   * @example
   * ```ts
   * // Remove single value
   * removeFilter({ code: 'color', id: 'blue' })
   *
   * // Remove entire filter
   * removeFilter({ code: 'price', id: undefined })
   * ```
   *
   * @remarks
   * - Triggers GTM `plpPage.onRemoveFilter`
   * - For PriceInput filters, clears entire filter (no individual value removal)
   * - Automatically removes filter from `filtersQuery` if last value is removed
   */
  const removeFilter = (filterToRemove: { code: string, id?: string }) => {
    const { code, id } = filterToRemove
    const filter = filters.value[code]
    const removedId = id || filter.selected

    if (isPriceFilter(filter) || isSearchFilter(filter)) {
      $gtm.push('plpPage.onRemoveFilter', code, filter.facetId, removedId)
    }
    else {
      const { label } = filter.options.find((option) => option.id === id)!
      $gtm.push('plpPage.onRemoveFilter', filter.label, filter.facetId, label)
    }

    filtersQuery.value[code] = filter.type === 'PriceInput'
      ? []
      : filtersQuery.value[code]?.filter((item: string) => item !== id)

    if (!filtersQuery.value[code].length) delete filtersQuery.value[code]
    onFiltersChange()
  }

  /**
   * Clears all active filters and updates the URL.
   *
   * @example
   * ```ts
   * clearFilters()
   * ```
   *
   * @remarks
   * - Triggers GTM `plpPage.onClearFilters` with category name (or 'All' if multiple filters active)
   * - Clears `filtersQuery` ref and removes `?filters` from URL
   */
  const clearFilters = () => {
    const selectedFilters = Object.values(filters.value).filter((item) => item.selected?.length)
    const category = selectedFilters?.length === 1 ? selectedFilters[0].label : 'All'

    $gtm.push('plpPage.onClearFilters', category)
    filtersQuery.value = {}
    storeFilterState.value = undefined
    onFiltersChange()
  }

  /**
   * Computed map of sort values to labels (for GTM tracking).
   *
   * @example
   * ```ts
   * { 'price-asc': 'Price: Low to High', 'newest': 'Newest' }
   * ```
   */
  const sortLabelByValue = computed(() => Object.fromEntries(
    sortOptions.value.map(({ label, value }) => [value, label])
  ))

  /**
   * Applies the current `sortState` value and updates the URL.
   *
   * @example
   * ```vue
   * <select v-model="sortState" @change="sortBy">
   *   <option v-for="opt in sortOptions" :value="opt.value">{{ opt.label }}</option>
   * </select>
   * ```
   *
   * @remarks
   * - Triggers GTM `plpPage.onSort` with sort label
   * - Updates `?sort` query param
   */
  const sortBy = () => {
    $gtm.push('plpPage.onSort', sortLabelByValue.value[sortState.value])
    onFiltersChange()
  }

  /**
   * Validates `sortState` against available `sortOptions` and resets to `defaultSort` if invalid.
   *
   * @remarks
   * Runs reactively whenever `sortOptions` or `sortState` changes (e.g., region switch, feature flag toggle)
   */
  watchEffect(() => {
    const isValid = sortOptions.value.some(({ value }) => value === sortState.value)
    if (!isValid) sortState.value = defaultSort
  })

  /**
   * Lifecycle hook: syncs state from URL when component is activated from keep-alive cache.
   *
   * @remarks
   * - Clears leftover filters if URL has no `?filters` param (e.g., breadcrumb navigation back to PLP root)
   * - Syncs `sortState` and `storeFilterState` from URL
   */
  onActivated(() => {
    // Clear leftover filters in the keep-alive cache when navigating back to the same PLP with a filter-less URL (e.g. breadcrumbs)
    if (!route.query.filters && Object.values(filters.value).some((item) => item.selected?.length))
      clearFilters()

    // update sort and storefilter based on querystring
    sortState.value = (route.query.sort || defaultSort) as string
    storeFilterState.value = route.query.storeFilter?.toString() || undefined
  })

  return {
    clearFilters,
    filterBy,
    filterByStore,
    filters,
    filtersQuery,
    removeFilter,
    sortBy,
    sortOptions,
    sortState,
    storeFilterState
  }
}
